<template>
  <div
    ref="breadcrumbsContainer"
    class="sui-breadcrumbs"
    >
    <a v-if="backButtonIsVisible" href="#" class="sui-breadcrumbs__button-back" @click.prevent="back">
      <svg
        class="sui-breadcrumbs__button-back-elem"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M18.9998 11.23C19.414 11.23 19.7498 11.5658 19.7498 11.98C19.7498 12.3597 19.4676 12.6735 19.1016 12.7232L18.9998 12.73H4.00977C3.59556 12.73 3.25977 12.3942 3.25977 11.98C3.25977 11.6003 3.54192 11.2865 3.908 11.2368L4.00977 11.23H18.9998Z"
          fill="#0D1140"
        />
        <path
          d="M9.48258 5.4575C9.77544 5.16459 10.2504 5.16455 10.5433 5.45742C10.8096 5.72366 10.8338 6.14032 10.616 6.43395L10.5434 6.51808L5.06195 11.9998L10.5434 17.4815C10.8096 17.7478 10.8338 18.1645 10.6159 18.4581L10.5433 18.5422C10.277 18.8084 9.86028 18.8326 9.56669 18.6147L9.48258 18.5421L3.47158 12.5301C3.20536 12.2638 3.18116 11.8472 3.39897 11.5536L3.47158 11.4695L9.48258 5.4575Z"
          fill="#0D1140"
        />
      </svg>
    </a>
    <div v-if="hasAdditionalIcon" class="sui-breadcrumbs__icon">
      <slot name="additional-icon"></slot>
    </div>
    <div v-if="isReady" class="sui-breadcrumbs__list">
      <template v-for="(item, index) in renderedBreadcrumbs" :key="index">
        <div v-if="index === 1 && collapsedItems.length" class="sui-breadcrumbs__item">
          <div
            class="sui-breadcrumbs__link"
            @mouseenter="onMouseEnter"
            @mouseleave="onMouseLeave"
            @click="toggleDropdown"
          >
            <div class="sui-breadcrumbs__divider">
              <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.33325 13.9662L11.6666 10.6329L8.33325 7.29956" stroke="#87898F" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="sui-breadcrumbs__text">...</div>
            <div v-if="dropdownVisible" class="breadcrumbs-dropdown font-body-s-400">
              <ul>
                <li
                  v-for="(crumb, idx) in collapsedItems"
                  :key="idx"
                  @click.stop="onSelectBreadcrumb(crumb)"
                >
                  <a :href="crumb.path" @click.prevent>{{ crumb.name }}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="sui-breadcrumbs__item">
          <Tooltip
            :text="item.name"
            placement="bottom"
            :disabled="tooltipIsDisabled(item.name, renderedItemsWidths[index], index + 1 === renderedBreadcrumbs.length)"
            trigger="hover"
            :delay="400"
          >
            <template #text>
              <a :key="index" :href="item.path" class="sui-breadcrumbs__link" @click.prevent="emit('navigate', item)">
                <div class="sui-breadcrumbs__divider">
                  <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.33325 13.9662L11.6666 10.6329L8.33325 7.29956" stroke="#87898F" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div class="sui-breadcrumbs__text" :style="{width: `${renderedItemsWidths[index]}px`}">
                  {{item.name}}
                </div>
              </a>
            </template>
          </Tooltip>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Tooltip from "../tooltip/Tooltip.vue";
import { BreadcrumbItem } from "./types";
import { computed, useSlots, ref, onMounted, onBeforeUnmount } from "vue";
import { round10 } from "@/helpers/text-helper";

const props = defineProps({
  breadcrumbs: {
    type: Array<BreadcrumbItem>,
    default: () => [],
  },
  backButtonIsVisible:{
    type:Boolean,
    default: false
  }
})

const slots = useSlots()
const emit = defineEmits<{
  navigate: [item: BreadcrumbItem]
  back: []
}>()

const hasAdditionalIcon = computed(() => slots['additional-icon'])
const canvas = document.createElement('canvas');
const context = canvas.getContext('2d')!;

const breadcrumbsContainer = ref<HTMLDivElement | null>(null)
const dropdownVisible = ref(false)
const renderedItemsWidths = ref<number[]>([])
const renderedBreadcrumbs = ref<BreadcrumbItem[]>([])
const collapsedItems = ref<BreadcrumbItem[]>([])
const isReady = ref(false)

const COLLAPSED_ITEM_WIDTH = 34.6
let MIN_ITEM_WIDTH = 170
let isTouchDeviceDetected = false
const ICON_WIDTH = 32

function normalizeItem(item: BreadcrumbItem) {
  const {name, ...rest} = item
  return {
    ...rest,
    name: name.replaceAll(/\p{C}/gu, '')
  }
}

function tooltipIsDisabled(name: string, renderedWidth: number, isLast: boolean) {
  const intrinsicWidth = getTextWidth(name, isLast ? '500 14px Rubik' : '400 14px Rubik')
  return intrinsicWidth === renderedWidth
}

function back() {
  emit('back')
}

function getNamesWidth(names: string[]) {
  return names.map((v, idx) => getTextWidth(v, idx + 1 === names.length ? '500 14px Rubik' : '400 14px Rubik'))
}

function isTouchDevice() {
  return (('ontouchstart' in window) ||
    (navigator.maxTouchPoints > 0) ||
    // @ts-ignore
    (navigator.msMaxTouchPoints > 0));
}

function toggleDropdown() {
  console.log('toggle')
  if (isTouchDeviceDetected) dropdownVisible.value = !dropdownVisible.value
}

function onMouseEnter() {
  if (!isTouchDeviceDetected) dropdownVisible.value = true
}
function onMouseLeave() {
  if (!isTouchDeviceDetected) dropdownVisible.value = false
}

function fitBreadcrumbs() {
  if (window.innerWidth <= 740) MIN_ITEM_WIDTH = 130
  isTouchDeviceDetected = isTouchDevice()
  const container = breadcrumbsContainer.value
  const backButtonWidth = !props.backButtonIsVisible ? 0 : ICON_WIDTH
  const iconWidth = hasAdditionalIcon.value ? ICON_WIDTH : 0
  const containerWidth = container!.clientWidth - backButtonWidth - iconWidth
  let itemsWidths = getNamesWidth(renderedBreadcrumbs.value.map(v => v.name))
  let breadcrumbsWidth = calculateBreadcrumbsWidth(itemsWidths)

  if (breadcrumbsWidth < containerWidth) {
    renderedItemsWidths.value = itemsWidths
    return
  }

  const spaceRequired = breadcrumbsWidth - containerWidth
  const availableSpace = calculateAvailableSpace(itemsWidths)
  if (availableSpace > spaceRequired) {
    renderedItemsWidths.value = reduceItemsWidths(itemsWidths, spaceRequired)
    return
  }
  if (renderedBreadcrumbs.value.length <= 2) return
  const removedBcItem = renderedBreadcrumbs.value.splice(1, 1)
  collapsedItems.value.push(removedBcItem[0])
  fitBreadcrumbs()
}

function calculateAvailableSpace(items: number[]) {
  return items.filter(v => v > MIN_ITEM_WIDTH).reduce((acc, item) => acc + item - MIN_ITEM_WIDTH, 0)
}

function reduceItemsWidths(itemsWidths: number[], spaceRequired: number): number[] {
  const reducedItemsWidths = [...itemsWidths]
  const itemsToResize = itemsWidths
    .map((width, idx) => ({width, idx, availableWidthReduction: width - MIN_ITEM_WIDTH}))
    .filter(v => v.availableWidthReduction > 0)
    .sort((a, b) => b.width > a.width ? 1 : -1)

  if (itemsToResize.length === 1) {
    const targetIdx = itemsToResize[0].idx
    reducedItemsWidths[targetIdx] = itemsToResize[0].width - spaceRequired
    return reducedItemsWidths
  }
  let idx = 0
  while (idx < itemsToResize.length) {
    const resizeFrame = itemsToResize.slice(0, idx + 1)
    const minimalEl = itemsToResize[idx + 1] ?? itemsToResize[itemsToResize.length - 1]
    const minimalValue = minimalEl.width
    const freeSpace = resizeFrame.reduce((acc, v) => acc + v.width - minimalValue, 0)

    if (freeSpace >= spaceRequired || idx === itemsToResize.length - 1) {
      const spaceRequiredPerItem = spaceRequired / (idx + 1)
      const reducedWidths = resizeFrame.map(v => v.width - spaceRequiredPerItem)
      const avg = reducedWidths.reduce((acc, item) => acc + item, 0) / (idx + 1)
      resizeFrame.forEach(resizeFrameItem => {
        const targetIdx = resizeFrameItem.idx
        reducedItemsWidths[targetIdx] = round10(avg, -2)
      })
      return reducedItemsWidths
    }
    idx += 1
  }
  return reducedItemsWidths
}

function calculateBreadcrumbsWidth(namesWidths: number[]) {
  const totalItemsWidth = namesWidths.reduce((acc, item) => acc + item, 0)
  const itemsCount = renderedBreadcrumbs.value.length
  const GAP_BETWEEN_ITEMS = 28
  let bcWidth = round10(GAP_BETWEEN_ITEMS * (itemsCount - 1) + totalItemsWidth, -2)
  if (collapsedItems.value.length) bcWidth += COLLAPSED_ITEM_WIDTH
  return bcWidth
}

function handleClickOutside(event: MouseEvent) {
  if (!dropdownVisible.value) return
  const container = breadcrumbsContainer.value
  if (container && !container.contains(event.target as Node)) {
    dropdownVisible.value = false
  }
}

function onSelectBreadcrumb(crumb: BreadcrumbItem) {
  emit('navigate', crumb)
  dropdownVisible.value = false
}

function getTextWidth(text: string, font = '400 14px "Rubik') {
  context.font = font
  const metrics = context.measureText(text);
  return round10(metrics.width, -2);
}

onMounted(() => {
  window.addEventListener('resize', fitBreadcrumbs)
  document.addEventListener('click', handleClickOutside)
  renderedBreadcrumbs.value = props.breadcrumbs.map(normalizeItem)
  fitBreadcrumbs()
  isReady.value = true
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', fitBreadcrumbs)
  document.removeEventListener('click', handleClickOutside)
})

</script>

<style scoped lang="scss">
.sui-breadcrumbs {
  width: 100%;
  max-width: 800px;
  font-size: 14px;
  line-height: 20px;

  @media (min-width: 1640px) {
    max-width: 1000px;
  }
  @media (min-width: 1920px) {
    max-width: 1200px;
  }
  display: flex;
  align-items: center;
  &__list {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  &__item {
    &:last-child {
      margin-right: 0;
      font-weight: 500;
    }
    &:first-child {
      .sui-breadcrumbs__divider {
        display: none;
      }
    }
  }
  &__link {
    display: flex;
    align-items: center;
    text-decoration: none;
    position: relative;
    cursor: pointer;
  }
  &__text {
    font-size: 14px;
    line-height: 20px;
    color: var(--color-text-primary);
    white-space: nowrap;
    //width: 100%;
    flex-basis: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    &--link {
      &:hover {
        text-decoration: underline;
      }
    }
  }
  &__divider {
    color: var(--color-text-primary);
    margin-right: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__icon {
    margin-right: 8px;
    &-elem {
      width: 24px;
      height: 24px;
      display: block;
    }
  }
  &__button-back {
    margin-right: 8px;
    &-elem {
      width: 24px;
      height: 24px;
      display: block;
    }
  }
}

.breadcrumbs-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  background: #fff;
  border: 1px solid var(--btn-border-disabled);
  box-shadow: 0 10px 10px 0 rgba(0, 0, 0, 0.05);
  z-index: 1000;
  padding: 4px 0;
  border-radius: 5px;
  width: 220px;
  color: var(--color-text-primary);
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    li {
      padding: 10px 16px;
      cursor: pointer;
      &:hover {
        background: var(--btn-border-disabled);
      }
      a {
        color: inherit;
        text-decoration: none;
      }
    }
  }
}
</style>
